import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import config from '../config';

/**
 * Context para gestión de estados de órdenes de servicio
 * Proporciona acceso centralizado a estados configurados de la empresa
 *
 * La información viene directamente de la base de datos:
 * - tp_estado_servicio (maestro de estados)
 * - tp_empresa_configuracion (estados activos y nombres personalizados)
 *
 * Este context es la ÚNICA fuente de verdad para estados en el frontend.
 * NO debe haber arrays hardcoded de estados en ningún componente.
 */
const EstadosContext = createContext();

export const EstadosProvider = ({ children }) => {
  const [estados, setEstados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  /**
   * Cargar estados desde el backend
   * Endpoint: GET /api/v1/configuracion/estados-activos
   */
  const fetchEstados = async () => {
    if (!user?.token) {
      console.log('EstadosContext - No hay token de usuario');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const url = `${config.API_BASE_URL}/api/v1/configuracion/estados-activos`;
      console.log('EstadosContext - Fetching desde:', url);
      console.log('EstadosContext - Token:', user.token ? 'Presente' : 'Ausente');
      console.log('EstadosContext - ID Empresa:', user.id_empresa);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      console.log('EstadosContext - Response completa:', response);
      console.log('EstadosContext - Response.data:', response.data);
      console.log('EstadosContext - Response.data.success:', response.data.success);
      console.log('EstadosContext - Response.data.data:', response.data.data);

      if (response.data.success) {
        // Protección: Si nombre está vacío, usar nombre_default
        const estadosData = (response.data.data || []).map(estado => ({
          ...estado,
          nombre: estado.nombre && estado.nombre.trim() !== '' ? estado.nombre : estado.nombre_default
        }));
        console.log('EstadosContext - Estados a setear:', estadosData);
        console.log('EstadosContext - Cantidad de estados:', estadosData.length);
        setEstados(estadosData);
      } else {
        throw new Error(response.data.error || 'Error al cargar estados');
      }
    } catch (error) {
      console.error('EstadosContext - Error cargando estados:', error);
      console.error('EstadosContext - Error completo:', error.response || error);
      setError(error.message);
      setEstados([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar estados cuando el usuario cambia
  useEffect(() => {
    fetchEstados();
  }, [user]);

  /**
   * Helper: Obtener nombre de estado por ID
   * Usa el nombre personalizado si existe, sino el default
   */
  const obtenerNombreEstado = (idEstado) => {
    const estado = estados.find(e => e.id === parseInt(idEstado));
    return estado?.nombre || estado?.nombre_default || `Estado ${idEstado}`;
  };

  /**
   * Helper: Obtener color de estado por ID
   * Devuelve el color desde la BD
   */
  const obtenerColorEstado = (idEstado) => {
    const estado = estados.find(e => e.id === parseInt(idEstado));
    return estado?.color || '#757575';
  };

  /**
   * Helper: Obtener estado completo por ID
   */
  const obtenerEstado = (idEstado) => {
    return estados.find(e => e.id === parseInt(idEstado));
  };

  /**
   * Helper: Verificar si un estado es personalizado
   */
  const esEstadoPersonalizado = (idEstado) => {
    const estado = estados.find(e => e.id === parseInt(idEstado));
    return estado?.es_personalizado || false;
  };

  /**
   * Helper: Obtener orden de un estado
   */
  const obtenerOrdenEstado = (idEstado) => {
    const estado = estados.find(e => e.id === parseInt(idEstado));
    return estado?.orden || 0;
  };

  return (
    <EstadosContext.Provider value={{
      estados,
      loading,
      error,
      fetchEstados,
      obtenerNombreEstado,
      obtenerColorEstado,
      obtenerEstado,
      esEstadoPersonalizado,
      obtenerOrdenEstado
    }}>
      {children}
    </EstadosContext.Provider>
  );
};

/**
 * Hook para usar el context de estados
 * Debe usarse dentro de un EstadosProvider
 */
export const useEstados = () => {
  const context = useContext(EstadosContext);
  if (!context) {
    throw new Error('useEstados debe usarse dentro de EstadosProvider');
  }
  return context;
};

export default EstadosContext;
